/**
 * Shared metadata for the Settings UI.
 *
 * Each category appears as a row in the native list and as a sidebar item in
 * the web two-pane dialog. The order here is the order users see.
 */
import { DEVELOPER_PLATFORM_ENABLED } from '../../lib/launch-scope';

export type SettingsCategoryId =
  | 'account'
  | 'billing'
  | 'usage'
  | 'preferences'
  | 'privacy'
  | 'reports'
  | 'notifications'
  | 'dataManagement'
  | 'mcp'
  | 'connectedApps'
  | 'agentAccess'
  | 'memory'
  | 'connectors'
  | 'feedback'
  | 'about';

export interface SettingsCategoryMeta {
  id: SettingsCategoryId;
  /** i18next key (under common) for the display label */
  labelKey: string;
  /**
   * Semantic icon name resolved by the shared Hugeicons contract, except
   * `uaePass`: that row carries the official UAE PASS thumbprint rather than a
   * Hugeicons glyph, so it is deliberately absent from the glyph maps and
   * resolved by `SettingsCategoryIcon`. Same carve-out the onboarding presets
   * make for `flag-uae`.
   */
  icon:
    | 'user'
    | 'creditCard'
    | 'usage'
    | 'sliders'
    | 'bell'
    | 'shieldCheck'
    | 'flag'
    | 'keyRound'
    | 'link'
    | 'bot'
    | 'brain'
    | 'messageSquare'
    | 'plug'
    | 'info'
    | 'uaePass';
  /**
   * Where the row goes. Normally a sub-route under app/settings/; when
   * `external` is set it is a full app destination outside the settings
   * navigator instead (see `external`).
   */
  nativeRoute: string;
  /**
   * Native-only: dismiss the Settings sheet before navigating to this route.
   */
  external?: boolean;
}

export const SETTINGS_CATEGORIES: SettingsCategoryMeta[] = [
  {
    id: 'account',
    labelKey: 'settings.categories.account',
    icon: 'user',
    nativeRoute: '/settings/account',
  },
  {
    id: 'billing',
    labelKey: 'settings.categories.billing',
    icon: 'creditCard',
    nativeRoute: '/settings/billing',
  },
  {
    id: 'usage',
    labelKey: 'settings.categories.usage',
    icon: 'usage',
    nativeRoute: '/settings/usage',
  },
  {
    id: 'preferences',
    labelKey: 'settings.categories.preferences',
    icon: 'sliders',
    nativeRoute: '/settings/preferences',
  },
  {
    id: 'privacy',
    labelKey: 'settings.privacy.title',
    icon: 'shieldCheck',
    nativeRoute: '/settings/privacy',
  },
  {
    id: 'dataManagement',
    labelKey: 'settings.dataManagement.title',
    icon: 'sliders',
    nativeRoute: '/settings/data-management',
  },
  {
    id: 'reports',
    labelKey: 'userReports.title',
    icon: 'flag',
    nativeRoute: '/settings/reports',
  },
  {
    id: 'notifications',
    labelKey: 'settings.categories.notifications',
    icon: 'bell',
    nativeRoute: '/settings/notifications',
  },
  {
    id: 'mcp',
    labelKey: 'settings.mcpAccess.title',
    icon: 'keyRound',
    nativeRoute: '/settings/mcp',
  },
  {
    id: 'connectedApps',
    labelKey: 'settings.connectedApps.title',
    icon: 'link',
    nativeRoute: '/settings/connected-apps',
  },
  {
    id: 'agentAccess',
    labelKey: 'settings.categories.agentAccess',
    icon: 'bot',
    nativeRoute: '/settings/agent-access',
  },
  {
    id: 'memory',
    labelKey: 'settings.aiMemory',
    icon: 'brain',
    nativeRoute: '/settings/memory',
  },
  {
    id: 'connectors',
    labelKey: 'nav.connectors',
    icon: 'plug',
    nativeRoute: '/settings/connectors',
  },
  {
    id: 'feedback',
    labelKey: 'feedback.floatingLabel',
    icon: 'messageSquare',
    nativeRoute: '/settings/feedback',
  },
  {
    id: 'about',
    labelKey: 'settings.about',
    icon: 'info',
    nativeRoute: '/settings/about',
  },
];

/**
 * Categories that exist only while the developer platform ships. Gated here
 * rather than at each call site so native and web cannot disagree: a build
 * without "Sign in with Tabbio" must not list apps a user has no way to have
 * connected.
 */
const DEVELOPER_PLATFORM_CATEGORIES: readonly SettingsCategoryId[] = [
  'connectedApps',
];

export function isSettingsCategoryAvailable(id: SettingsCategoryId) {
  return (
    DEVELOPER_PLATFORM_ENABLED || !DEVELOPER_PLATFORM_CATEGORIES.includes(id)
  );
}

/** `SETTINGS_CATEGORIES` minus anything this build does not ship. */
export function getVisibleSettingsCategories() {
  return SETTINGS_CATEGORIES.filter((category) =>
    isSettingsCategoryAvailable(category.id),
  );
}

/**
 * The category a settings href lands on, or null when the href is not a
 * category page (job alerts, the account editor, ...). Lets the web dialog
 * keep an in-dialog link such as Data management -> Memory inside the dialog
 * instead of closing it and navigating. Route groups and queries are ignored;
 * `/connectors` is the app-wide Connectors destination the shared panel uses.
 */
export function findSettingsCategoryByHref(
  href: string,
): SettingsCategoryId | null {
  const path = href
    .trim()
    .split(/[?#]/)[0]
    .replace(/^\/\((?:app|employer)\)(?=\/|$)/, '')
    .replace(/\/+$/, '');
  if (path === '/connectors') return 'connectors';
  const match = SETTINGS_CATEGORIES.find(
    (category) => category.nativeRoute === path,
  );
  return match && isSettingsCategoryAvailable(match.id) ? match.id : null;
}

/** Compact menus use the same complete category set as the desktop sidebar. */
export const SETTINGS_CATEGORY_GROUPS: {
  labelKey: string;
  ids: SettingsCategoryId[];
}[] = [
  { labelKey: 'settings.groups.account', ids: ['account', 'billing', 'usage', 'preferences', 'privacy', 'dataManagement'] },
  { labelKey: 'settings.groups.activity', ids: ['reports', 'notifications'] },
  { labelKey: 'settings.groups.access', ids: ['mcp', 'connectedApps', 'agentAccess', 'memory', 'connectors'] },
  { labelKey: 'settings.groups.help', ids: ['feedback', 'about'] },
];

export function getSettingsCategoryGroups() {
  const visible = getVisibleSettingsCategories();
  return SETTINGS_CATEGORY_GROUPS.map((group) => ({
    labelKey: group.labelKey,
    categories: group.ids.flatMap((id) => visible.filter((category) => category.id === id)),
  })).filter((group) => group.categories.length > 0);
}
